import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Chip,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import {
  updateExistingTask,
  fetchTasks,
} from "store/features/company/projectManagement/task/taskActions";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
} from "store/features/company/projectManagement/project/projectSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import {
  selectTasks,
  selectTaskLoading,
} from "store/features/company/projectManagement/task/taskSelectors";
import editTaskSvg from "assets/modals/edit.svg";

const EditTaskModal = ({ isOpen, onClose, task, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const projects = useSelector(selectProjects);
  const projectsLoading = useSelector(selectProjectLoading);
  const employees = useSelector(selectEmployees);
  const employeesLoading = useSelector(selectEmployeeLoading);
  const tasks = useSelector(selectTasks);
  const tasksLoading = useSelector(selectTaskLoading);
  const [parentTaskOptions, setParentTaskOptions] = useState([]);
  const [formData, setFormData] = useState({
    project_id: "",
    parent_task_id: "",
    title: "",
    description: "",
    assigned_to: "",
    priority: "medium",
    status: "todo",
    estimated_hours: "",
    start_date: "",
    due_date: "",
    completed_at: "",
    labels: [],
  });
  const [labelInput, setLabelInput] = useState("");

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProjects());
      dispatch(fetchEmployees());
      dispatch(fetchTasks());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
  if (formData.project_id) {
    const filteredTasks = tasks.filter(
      (task) => task.project_id == formData.project_id && task.id !== formData.id
    );
        setParentTaskOptions(filteredTasks);
  } else {
    setParentTaskOptions([]);
  }
}, [formData.project_id, tasks]);

  //scroll bar
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    if (task) {
      setFormData({
        project_id: task.project_id || "",
        parent_task_id: task.parent_task_id || "",
        title: task.title || "",
        description: task.description || "",
        assigned_to: task.assigned_to || "",
        priority: task.priority || "medium",
        status: task.status || "todo",
        estimated_hours: task.estimated_hours || "",
        start_date: task.start_date ? task.start_date.split("T")[0] : "",
        due_date: task.due_date ? task.due_date.split("T")[0] : "",
        completed_at: task.completed_at ? task.completed_at.split("T")[0] : "",
        labels: task.labels || [],
      });
    }
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddLabel = () => {
    if (labelInput.trim() && !formData.labels.includes(labelInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        labels: [...prev.labels, labelInput.trim()],
      }));
      setLabelInput("");
    }
  };

  const handleRemoveLabel = (labelToRemove) => {
    setFormData((prev) => ({
      ...prev,
      labels: prev.labels.filter((label) => label !== labelToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.project_id) {
      return;
    }

    setLoading(true);
    const submitData = {
      project_id: parseInt(formData.project_id),
      title: formData.title,
      description: formData.description,
      assigned_to: formData.assigned_to
        ? parseInt(formData.assigned_to)
        : undefined,
      priority: formData.priority,
      estimated_hours: formData.estimated_hours
        ? parseFloat(formData.estimated_hours)
        : 0,
      start_date: formData.start_date,
      due_date: formData.due_date,
      labels: formData.labels,
    };

    // Only include optional fields if they have values
    if (formData.parent_task_id) {
      submitData.parent_task_id = parseInt(formData.parent_task_id);
    }
    if (formData.completed_at) {
      submitData.completed_at = formData.completed_at;
    }

    const result = await dispatch(updateExistingTask(task.id, submitData));

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && task && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editTaskSvg}
                      alt="Edit Task"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Edit Task
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Update task information
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Project"
                      name="project_id"
                      value={formData.project_id}
                      onChange={handleChange}
                      required
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      disabled={projectsLoading}
                    >
                      <MenuItem value="">
                        <em>Select a project</em>
                      </MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name} ({project.code})
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      fullWidth
                      select
                      label="Parent Task"
                      name="parent_task_id"
                      value={formData.parent_task_id}
                      onChange={handleChange}
                      placeholder="Optional"
                      helperText="Select parent task if this is a subtask"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      disabled={!formData.project_id || tasksLoading}
                      key={tasks.length}
                    >
                      <MenuItem value="">
                        <em>No parent task</em>
                      </MenuItem>
                      {parentTaskOptions.map((task) => (
                        <MenuItem key={task.id} value={task.id}>
                          {task.title} (ID: {task.id})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <TextField
                    fullWidth
                    label="Task Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter task title"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the task"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Assign To"
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      disabled={employeesLoading}
                    >
                      <MenuItem value="">
                        <em>Select an employee</em>
                      </MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_code})
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      fullWidth
                      label="Estimated Hours"
                      name="estimated_hours"
                      type="number"
                      value={formData.estimated_hours}
                      onChange={handleNumberChange}
                      placeholder="10"
                      helperText="Estimated hours to complete"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="todo">To Do</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="review">Review</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      label="Due Date"
                      name="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  {formData.status === "completed" && (
                    <TextField
                      fullWidth
                      label="Completed Date"
                      name="completed_at"
                      type="date"
                      value={formData.completed_at}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}

                  {/* Labels Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                        fontWeight: 500,
                      }}
                    >
                      Labels
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        placeholder="Add labels..."
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#f9fafb",
                          },
                        }}
                      />
                      <Button
                        onClick={handleAddLabel}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "#f59e0b",
                          color: "#f59e0b",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Stack>
                    {formData.labels.length > 0 && (
                      <Stack
                        direction="row"
                        sx={{
                          mt: 1,
                          flexWrap: "wrap",
                          gap: 1,
                          overflowX: "hidden",
                        }}
                      >
                        {formData.labels.map((label, index) => (
                          <Chip
                            key={index}
                            label={label}
                            onDelete={() => handleRemoveLabel(label)}
                            size="small"
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#f3f4f6",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#374151",
                              "& .MuiChip-deleteIcon": {
                                color: "#f59e0b",
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading || !formData.title.trim() || !formData.project_id
                  }
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Task"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditTaskModal;
